import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import permissionsService from '../../services/permissionsService';
import api from '../../services/api';
import FileUploadZone from './FileUploadZone';
import { 
  ArrowLeft, 
  ArrowRight, 
  Globe, 
  Building2, 
  FlaskConical,
  FileText,
  Brain,
  BarChart3,
  Clock,
  CheckCircle2,
  Upload,
  Edit3,
  Copy,
  Languages,
  Smartphone,
  Eye,
  X,
  Layers,
  ListChecks
} from 'lucide-react';

const LibrarySelection = ({ onNext, onBack, fieldErrors, onClearFieldError }) => {
  const navigate = useNavigate();
  const { user, userPermissions } = useContext(AuthContext);
  
  // Primary/Variant selection
  const [variantMode, setVariantMode] = useState('primary'); // 'primary' or 'variant'
  const [primaryAssessments, setPrimaryAssessments] = useState([]);
  const [loadingPrimaries, setLoadingPrimaries] = useState(false);
  const [selectedPrimary, setSelectedPrimary] = useState('');
  const [devices, setDevices] = useState({ phone: [], tablet: [], desktop: [] });
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [variantName, setVariantName] = useState('');
  const [variantError, setVariantError] = useState('');
  const [copyStructure, setCopyStructure] = useState(true);
  
  const [selectedLibrary, setSelectedLibrary] = useState('');
  const [creationMethod, setCreationMethod] = useState('manual'); // 'manual' or 'import'
  const [assessmentType, setAssessmentType] = useState('fixed_form');
  const [assessmentName, setAssessmentName] = useState('');
  const [description, setDescription] = useState('');
  const [therapeuticArea, setTherapeuticArea] = useState('');
  // FECOA-1526: respondent type chosen at creation (PRO / ObsRO / ClinRO).
  const [respondentType, setRespondentType] = useState('');
  const [respondentTypeError, setRespondentTypeError] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [availableStudies, setAvailableStudies] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [selectedSourceAssessment, setSelectedSourceAssessment] = useState('');
  const [canPublishToVPG, setCanPublishToVPG] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Duplicate name handling
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [duplicateNameData, setDuplicateNameData] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  
  // Local name error state (for inline validation display)
  const [nameError, setNameError] = useState('');

  // FECOA-1995: Copy from Existing prefills the Assessment Information block
  // from the source. Fields the user has typed into are never overwritten when
  // the source selection changes, so their edits survive a change of mind.
  const copyFieldsTouched = useRef({
    name: false,
    therapeuticArea: false,
    description: false,
    respondentType: false,
  });

  const libraryOptions = [
    {
      id: 'vpg',
      title: 'VPG Global Library',
      icon: Globe,
      description: 'Available to all sponsors and studies',
      color: 'bg-blue-500',
      available: canPublishToVPG,
      tooltip: canPublishToVPG ? '' : 'Requires special permission to publish to VPG library'
    },
    {
      id: 'sponsor', 
      title: 'Sponsor Library',
      icon: Building2,
      description: 'Available to all studies under your sponsor',
      color: 'bg-primary-500',
      available: true,
      tooltip: ''
    },
    {
      id: 'study',
      title: 'Study-Specific Library', 
      icon: FlaskConical,
      description: 'Available only within selected study',
      color: 'bg-purple-500',
      available: true,
      tooltip: ''
    }
  ];

  const assessmentTypes = [
    {
      id: 'fixed_form',
      title: 'Fixed Form',
      icon: FileText,
      description: 'Traditional static questionnaire with predetermined items',
      recommended: 'Most common choice'
    },
    {
      id: 'item_bank',
      title: 'Item Library',
      icon: BarChart3,
      description: 'Create a short form from a library of items',
      recommended: 'Flexible item selection'
    }
  ];

  const commonLanguages = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'es-ES', label: 'Spanish (Spain)' },
    { code: 'es-MX', label: 'Spanish (Mexico)' },
    { code: 'fr-FR', label: 'French (France)' },
    { code: 'de-DE', label: 'German (Germany)' },
    { code: 'it-IT', label: 'Italian (Italy)' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'ja-JP', label: 'Japanese (Japan)' },
    { code: 'zh-CN', label: 'Chinese (Simplified)' },
    { code: 'ko-KR', label: 'Korean (South Korea)' }
  ];

  // Check VPG library permission on mount
  useEffect(() => {
    const checkVPGPermission = async () => {
      const hasPermission = await permissionsService.hasPermission(
        permissionsService.PERMISSIONS.PUBLISH_TO_VPG_LIBRARY
      );
      setCanPublishToVPG(hasPermission);
    };
    checkVPGPermission();
  }, []);

  // Fetch devices and primaries on mount
  useEffect(() => {
    if (variantMode === 'variant') {
      fetchPrimaries();
      fetchDevices();
    }
  }, [variantMode]);

  const fetchPrimaries = async () => {
    setLoadingPrimaries(true);
    try {
      const response = await api.get('/api/assessment-builder/assessments/');
      const data = response.data;
      const primaries = (data.results || data || []).filter(a => !a.parent_assessment);
      setPrimaryAssessments(primaries);
    } catch (error) {
      console.error('Error fetching primary assessments:', error);
    } finally {
      setLoadingPrimaries(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await api.get('/api/assessment-builder/devices/');
      const data = response.data;
      setDevices(data.grouped || { phone: [], tablet: [], desktop: [] });
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  };

  // Fetch assessments that can be copied
  const fetchAssessments = async () => {
    setLoadingAssessments(true);
    try {
      const response = await api.get('/api/assessment-builder/assessments/');
      const data = response.data;
      let assessments = [];
      if (Array.isArray(data)) {
        assessments = data;
      } else if (data.results && Array.isArray(data.results)) {
        assessments = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        assessments = data.data;
      }
      
      // Show all assessments (will be grouped by status in dropdown)
      setAvailableAssessments(assessments);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAvailableAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  // Fetch studies that the current user has access to
  const fetchStudies = async () => {
    setLoadingStudies(true);
    try {
      const response = await api.get('/api/studies/');
      const data = response.data;
      
      // Handle different response formats
      let studies = [];
      if (Array.isArray(data)) {
        studies = data;
      } else if (data.results && Array.isArray(data.results)) {
        studies = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        studies = data.data;
      } else {
        console.warn('Unexpected studies response format:', data);
      }
      
      setAvailableStudies(studies);
    } catch (error) {
      console.error('Error fetching studies:', error);
      setAvailableStudies([]);
    } finally {
      setLoadingStudies(false);
    }
  };

  // Update validation logic to include study selection for study-specific library and variant
  const canProceed = variantMode === 'variant' 
    ? (selectedPrimary && selectedDevice && selectedLanguage)
    : (
      selectedLibrary && (
        selectedLibrary !== 'study' || selectedStudy
      ) && (
        (creationMethod === 'manual' && assessmentName.trim() && assessmentType) ||
        (creationMethod === 'import' && uploadedFile) ||
        (creationMethod === 'copy' && selectedSourceAssessment && assessmentName.trim())
      )
    );

  const handleFileUpload = async (file) => {
    setUploadError('');
    
    // Validate study selection for study-specific library
    if (selectedLibrary === 'study' && !selectedStudy) {
      setUploadError('Please select a study before uploading a CDISC file.');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('library_type', selectedLibrary);
      if (selectedLibrary === 'study' && selectedStudy) {
        formData.append('study_id', selectedStudy);
      }
      
      console.log('[CDISC IMPORT] Uploading file - format and version will be auto-detected');
      
      const response = await api.post('/api/assessment-builder/import/cdisc/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const result = response.data;
      
      console.log('[CDISC IMPORT] Import successful:', result);
      
      // Success! Navigate directly to completion or next step
      if (onNext) {
        onNext({
          mode: 'import',
          assessment_id: result.assessment_id,
          assessment_uuid: result.assessment_uuid || result.uuid,
          survey_id: result.survey_id,
          name: result.name,
          library_type: selectedLibrary,
          items_imported: result.items_imported,
          imported_items: result.imported_items || [],
          detected_version: result.detected_odm_version || result.cdisc_version
        });
      }
      
    } catch (error) {
      console.error('[CDISC IMPORT] Upload error:', error);
      
      // Check for duplicate name error (409 Conflict)
      if (error.response?.status === 409 && error.response?.data?.error_code === 'DUPLICATE_NAME') {
        const data = error.response.data;
        const userProvidedName = assessmentName && assessmentName.trim();
        setDuplicateNameData({
          file,
          originalName: data.original_name,
          suggestedName: data.suggested_name,
          errorMessage: data.error,
          userProvidedName: userProvidedName // Track if user already provided a name
        });
        setRenameValue(data.suggested_name || `${data.original_name} (Copy)`);
        setShowRenameModal(true);
        setUploadError('');
      } else {
        setUploadError(error.response?.data?.error || error.message);
      }
    } finally {
      setUploading(false);
    }
  };
  
  // Retry import with new name after duplicate detected
  const handleRetryWithNewName = async () => {
    if (!duplicateNameData?.file || !renameValue.trim()) return;
    
    setShowRenameModal(false);
    setUploading(true);
    setUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('file', duplicateNameData.file);
      formData.append('library_type', selectedLibrary);
      formData.append('name', renameValue.trim());
      if (selectedLibrary === 'study' && selectedStudy) {
        formData.append('study_id', selectedStudy);
      }
      
      console.log('[CDISC IMPORT] Retrying with new name:', renameValue.trim());
      
      const response = await api.post('/api/assessment-builder/import/cdisc/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const result = response.data;
      console.log('[CDISC IMPORT] Import successful with new name:', result);
      
      setDuplicateNameData(null);
      setRenameValue('');
      
      if (onNext) {
        onNext({
          mode: 'import',
          assessment_id: result.assessment_id,
          assessment_uuid: result.assessment_uuid || result.uuid,
          survey_id: result.survey_id,
          name: result.name,
          library_type: selectedLibrary,
          items_imported: result.items_imported,
          imported_items: result.imported_items || [],
          detected_version: result.detected_odm_version || result.cdisc_version
        });
      }
    } catch (error) {
      console.error('[CDISC IMPORT] Retry error:', error);
      
      // Check if still a duplicate name error
      if (error.response?.status === 409 && error.response?.data?.error_code === 'DUPLICATE_NAME') {
        setRenameValue(error.response.data.suggested_name || `${renameValue} (Copy)`);
        setShowRenameModal(true);
        setUploadError('That name is also taken. Please try another.');
      } else {
        setUploadError(error.response?.data?.error || error.message);
        setDuplicateNameData(null);
      }
    } finally {
      setUploading(false);
    }
  };

  const fetchAssessmentPreview = async (assessmentId) => {
    setLoadingPreview(true);
    setPreviewData(null);
    
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/`);
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error fetching assessment preview:', error);
      setUploadError('Failed to load assessment preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // FECOA-1995: picking a source assessment seeds name, COA type, therapeutic
  // area and description from it. The list endpoint already returns all four,
  // so no extra request is needed.
  const handleSourceAssessmentChange = (uuid) => {
    setSelectedSourceAssessment(uuid);
    if (!uuid) return;

    const source = availableAssessments.find(a => a.uuid === uuid);
    if (!source) return;

    const touched = copyFieldsTouched.current;
    if (!touched.name) {
      setAssessmentName(source.name ? `Copy of ${source.name}` : '');
      setNameError('');
      setUploadError('');
    }
    if (!touched.respondentType && source.respondent_type) {
      setRespondentType(source.respondent_type);
      setRespondentTypeError('');
    }
    if (!touched.therapeuticArea) {
      setTherapeuticArea(source.therapeutic_area || '');
    }
    if (!touched.description) {
      setDescription(source.description || '');
    }
  };

  const handleCopyAssessment = async () => {
    if (!selectedSourceAssessment || !assessmentName.trim()) {
      return;
    }
    // FECOA-1526: respondent type is required when copying too.
    if (!respondentType) {
      setRespondentTypeError('Please select a COA type.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');

      const payload = {
        name: assessmentName.trim(),
        library_type: selectedLibrary,
        description: description.trim(),
        respondent_type: respondentType,
        // FECOA-1996: the form collects this, so it has to be sent — otherwise
        // the copy silently inherits the source's therapeutic area.
        therapeutic_area: therapeuticArea.trim(),
      };

      // Include study_id for study-specific assessments
      if (selectedLibrary === 'study' && selectedStudy) {
        payload.study_id = selectedStudy;
      }

      const response = await api.post(`/api/assessment-builder/assessments/${selectedSourceAssessment}/copy/`, payload);
      const copiedAssessment = response.data;
      
      // Navigate to screen builder with the copied assessment
      if (onNext) {
        onNext({
          mode: 'edit',
          assessment_id: copiedAssessment.id,
          assessment_uuid: copiedAssessment.uuid,
          survey_id: copiedAssessment.survey_id || copiedAssessment.survey?.id,
          name: copiedAssessment.name,
          library_type: copiedAssessment.library_type,
          assessment_type: copiedAssessment.assessment_type
        });
      }
    } catch (error) {
      console.error('Copy error:', error);
      // Extract error message from response data
      const errorData = error.response?.data;
      let errorMessage = 'Failed to copy assessment';
      if (errorData) {
        if (errorData.name) {
          errorMessage = errorData.name;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Reject rapid successive submits. State alone can't catch taps that arrive
  // within the same React tick, so the ref lets us no-op the second call
  // before any network request fires.
  const submitInFlightRef = useRef(false);

  const handleNext = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    // Clear previous errors
    setVariantError('');

    // Handle variant creation
    if (variantMode === 'variant') {
      setUploading(true);
      try {
        const requestBody = {
          device_id: parseInt(selectedDevice),
          language: selectedLanguage,
          copy_structure: copyStructure
        };
        
        // Only include variant_name if provided
        if (variantName && variantName.trim()) {
          requestBody.variant_name = variantName.trim();
        }
        
        const response = await api.post(
          `/api/assessment-builder/assessments/${selectedPrimary}/create-variant/`,
          requestBody
        );

        const data = response.data;
        console.log('✅ Variant created:', data);
        
        // Get the variant assessment details and call onNext
        const variantResponse = await api.get(`/api/assessment-builder/assessments/${data.variant_assessment_uuid || data.variant_assessment}/`);
        const variantData = variantResponse.data;
        const config = {
          assessment_id: variantData.id,
          assessment_uuid: variantData.uuid,
          survey_id: variantData.survey?.id || variantData.survey,
          name: variantData.name,
          library_type: variantData.library_type,
          assessment_type: variantData.assessment_type,
          mode: 'edit',
          ...variantData.builder_metadata
        };
        if (onNext) {
          onNext(config);
        }
      } catch (error) {
        console.error('❌ Error creating variant:', error);
        // Handle error response from api
        const errorData = error.response?.data;
        let errorMessage = errorData?.error || errorData?.message || errorData?.non_field_errors?.[0] || error.message || 'Failed to create variant';
        if (typeof errorMessage !== 'string') {
          errorMessage = JSON.stringify(errorMessage) || 'Failed to create variant';
        }
        
        if (errorMessage.toLowerCase().includes('already exists')) {
          setVariantError(
            `A variant for ${selectedLanguage} on this device already exists. ` +
            'Please enter a custom name below to create another variant with the same language/device combination.'
          );
        } else {
          setVariantError(errorMessage);
        }
      } finally {
        setUploading(false);
        submitInFlightRef.current = false;
      }
      return;
    }

    // Handle copy method
    if (creationMethod === 'copy') {
      try {
        await handleCopyAssessment();
      } finally {
        submitInFlightRef.current = false;
      }
      return;
    }

    // Handle manual creation - make API call directly to handle errors inline
    setNameError(''); // Clear previous errors
    // FECOA-1526: respondent type is required at creation.
    if (!respondentType) {
      setRespondentTypeError('Please select a COA type.');
      return;
    }
    setUploading(true);

    try {
      const payload = {
        survey_data: {
          name: assessmentName.trim(),
          language: 'en-US',
          description: description.trim(),
          long_description: '',
          respondent_type: respondentType
        },
        library_type: selectedLibrary,
        assessment_type: assessmentType,
        therapeutic_area: therapeuticArea.trim(),
        version: '1.0',
        builder_metadata: {}
      };

      // Include study_id for study-specific assessments
      if (selectedLibrary === 'study' && selectedStudy) {
        payload.study_id = selectedStudy;
      }

      console.log('Creating new assessment:', payload);
      const response = await api.post('/api/assessment-builder/assessments/', payload);
      const newAssessment = response.data;
      console.log('Created assessment:', newAssessment);

      // Success - call parent's onNext with the created assessment.
      // assessment_uuid MUST be forwarded — the parent navigates to
      // /assessment-builder/{uuid} and a missing uuid produces a "undefined"
      // route param, which triggers a bogus GET /assessments/undefined/ 400.
      if (onNext) {
        onNext({
          mode: 'edit',
          assessment_id: newAssessment.id,
          assessment_uuid: newAssessment.uuid,
          survey_id: newAssessment.survey_id || newAssessment.survey?.id,
          name: newAssessment.name || assessmentName.trim(),
          library_type: selectedLibrary,
          assessment_type: assessmentType,
          studies: newAssessment.studies || [],
          version: newAssessment.version,
          publication_status: newAssessment.publication_status,
          default_templates: newAssessment.default_templates,
        });
      }
    } catch (error) {
      console.error('Error creating assessment:', error);
      console.error('Error response data:', error.response?.data);
      
      const errorData = error.response?.data;
      const nameErr = errorData?.name || errorData?.survey_data?.name;
      
      if (nameErr) {
        // Show inline error for duplicate name
        const errorMessage = Array.isArray(nameErr) ? nameErr[0] : nameErr;
        console.log('Setting name error:', errorMessage);
        setNameError(errorMessage);
      } else {
        // Generic error
        const errorMsg = errorData?.error || errorData?.detail || error.message;
        setUploadError(`Failed to create assessment: ${errorMsg}`);
      }
    } finally {
      setUploading(false);
      submitInFlightRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 bg-primary-600">
            <h2 className="text-xl font-semibold text-white">Create New Assessment</h2>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Welcome Section */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 text-primary-600 rounded-full mb-4">
                <FileText className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Configure Assessment Settings</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Choose where to store your assessment and how you'd like to create it
              </p>
            </div>

            {/* Primary/Variant Mode Toggle */}
            <div className="mb-8">
              {/* FECOA-1526: relabeled from "Assessment Type" to match the mockup */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Creation Mode *</h3>
              <div className="flex space-x-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-2">
                <button
                  type="button"
                  onClick={() => {
                    setVariantMode('primary');
                    setVariantError('');
                  }}
                  className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                    variantMode === 'primary'
                      ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm dark:shadow-gray-900/20'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Create Primary Assessment
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setVariantMode('variant');
                    setVariantError('');
                  }}
                  className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                    variantMode === 'variant'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Languages className="w-4 h-4 inline mr-2" />
                  Create Variant
                </button>
              </div>
              {variantMode === 'variant' && (
                <div className="mt-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-purple-900">
                      <Languages className="w-4 h-4 inline mr-2" />
                      Variants are linked to their primary assessment
                    </p>
                    <p className="text-sm text-purple-800">
                      Use variants to create language translations or device-specific versions that maintain a connection to the original primary assessment. Ideal for multi-language studies or platform adaptations.
                    </p>
                    <p className="text-xs text-purple-700 italic">
                      💡 Looking to create an independent copy? Use "Create Primary" → "Copy from Existing" instead.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Conditionally render Primary flow or Variant form */}
            {variantMode === 'primary' ? (
              <>
            {/* Library Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Library Location *</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {libraryOptions.filter(option => option.available).map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = selectedLibrary === option.id;
                  
                  return (
                    <div
                      key={option.id}
                      onClick={() => {
                        setSelectedLibrary(option.id);
                        setSelectedStudy('');
                        if (option.id === 'study') {
                          fetchStudies();
                        }
                      }}
                      className={`
                        relative p-6 border rounded-xl cursor-pointer transition-all duration-300
                        ${isSelected 
                          ? 'border-primary-500 bg-primary-50 shadow-sm' 
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 hover:shadow-sm dark:shadow-gray-900/20'
                        }
                      `}
                    >
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{option.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="absolute top-3 right-3">
                          <CheckCircle2 className="w-5 h-5 text-primary-600" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Study Selection */}
              {selectedLibrary === 'study' && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Study *
                  </label>
                  {loadingStudies ? (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span>Loading studies...</span>
                    </div>
                  ) : (
                    <select 
                      value={selectedStudy}
                      onChange={(e) => setSelectedStudy(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Choose a study...</option>
                      {availableStudies.map((study) => (
                        <option key={study.id} value={study.id}>
                          {study.study_code} {study.title}
                        </option>
                      ))}
                    </select>
                  )}
                  {availableStudies.length === 0 && !loadingStudies && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 font-medium">No studies available</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Contact your administrator for access, or choose a different library type.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Creation Method Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">How would you like to create this assessment? *</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  onClick={() => setCreationMethod('manual')}
                  className={`
                    p-5 border rounded-xl cursor-pointer transition-all duration-300 flex items-start space-x-4
                    ${creationMethod === 'manual' 
                      ? 'border-primary-500 bg-primary-50 shadow-sm' 
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 hover:shadow-sm dark:shadow-gray-900/20'
                    }
                  `}
                >
                  <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Edit3 className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Build from Scratch</h4>
                      {creationMethod === 'manual' && (
                        <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Use the visual builder to create screens manually</p>
                  </div>
                </div>

                <div
                  onClick={() => {
                    setCreationMethod('copy');
                    fetchAssessments();
                  }}
                  className={`
                    p-5 border rounded-xl cursor-pointer transition-all duration-300 flex items-start space-x-4
                    ${creationMethod === 'copy' 
                      ? 'border-primary-500 bg-primary-50 shadow-sm' 
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 hover:shadow-sm dark:shadow-gray-900/20'
                    }
                  `}
                >
                  <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Copy className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Copy from Existing</h4>
                      {creationMethod === 'copy' && (
                        <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Creates independent copy with no link to original</p>
                  </div>
                </div>

                <div
                  onClick={() => setCreationMethod('import')}
                  className={`
                    p-5 border rounded-xl cursor-pointer transition-all duration-300 flex items-start space-x-4
                    ${creationMethod === 'import' 
                      ? 'border-primary-500 bg-primary-50 shadow-sm' 
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 hover:shadow-sm dark:shadow-gray-900/20'
                    }
                  `}
                >
                  <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Upload className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Import from File</h4>
                      {creationMethod === 'import' && (
                        <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Upload a CDISC ODM XML or CSV file</p>
                  </div>
                </div>
              </div>

              {/* Copy from Existing Selection */}
              {creationMethod === 'copy' && (
                <div className="mt-6 space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Copy className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900 mb-1">
                          📋 Copy from Existing Assessment
                        </p>
                        <p className="text-sm text-blue-800 mb-2">
                          Creates a <strong>completely independent copy</strong> with no link to the original. All screens, questions, and settings will be duplicated. You'll have full ownership to modify as needed.
                        </p>
                        <p className="text-xs text-blue-700 italic">
                          💡 Use this when: Starting a new custom assessment based on an existing one, or creating a standalone version that won't be affected by changes to the original.
                        </p>
                        <div className="mt-2 pt-2 border-t border-blue-200">
                          <p className="text-xs text-blue-700">
                            <strong>Need a linked version instead?</strong> Use "Create Variant" for language translations or device adaptations that should stay connected to a primary.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Source Assessment *
                    </label>
                    {loadingAssessments ? (
                      <div className="flex items-center space-x-2 text-sm text-gray-600 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                        <span>Loading assessments...</span>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <select 
                          value={selectedSourceAssessment}
                          onChange={(e) => handleSourceAssessmentChange(e.target.value)}
                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Choose an assessment to copy...</option>
                        
                        {/* Published Assessments */}
                        {availableAssessments.filter(a => a.publication_status === 'published').length > 0 && (
                          <optgroup label="✓ Published Assessments">
                            {availableAssessments
                              .filter(a => a.publication_status === 'published')
                              .map((assessment) => (
                                <option key={assessment.uuid} value={assessment.uuid}>
                                  {assessment.name} (v{assessment.version}) - {assessment.library_type}
                                </option>
                              ))}
                          </optgroup>
                        )}
                        
                        {/* Draft Assessments */}
                        {availableAssessments.filter(a => a.publication_status !== 'published').length > 0 && (
                          <optgroup label="📝 Draft Assessments">
                            {availableAssessments
                              .filter(a => a.publication_status !== 'published')
                              .map((assessment) => (
                                <option key={assessment.uuid} value={assessment.uuid}>
                                  {assessment.name} (v{assessment.version}) - {assessment.library_type}
                                </option>
                              ))}
                          </optgroup>
                        )}
                        </select>
                        
                        {/* Preview Button */}
                        {selectedSourceAssessment && (
                          <button
                            type="button"
                            onClick={() => fetchAssessmentPreview(selectedSourceAssessment)}
                            disabled={loadingPreview}
                            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            title="View Assessment Summary"
                          >
                            {loadingPreview ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                <span>Loading...</span>
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4" />
                                <span>Preview</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                    {availableAssessments.length === 0 && !loadingAssessments && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800 font-medium">No assessments available</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Create an assessment first, then it will appear here for copying.
                        </p>
                      </div>
                    )}
                  </div>

                  {selectedSourceAssessment && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-1">
                        💡 Provide a New Assessment Name
                      </p>
                      <p className="text-sm text-blue-800">
                        The selected assessment will be duplicated with all its screens and questions. Please provide a unique name for the new copy below.
                      </p>
                      {availableAssessments.find(a => a.uuid === selectedSourceAssessment)?.publication_status !== 'published' && (
                        <p className="text-xs text-amber-700 mt-2">
                          ⚠️ Note: You're copying a draft assessment. Consider publishing the original first if you want a stable starting point.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* File Upload Zone */}
              {creationMethod === 'import' && (
                <div className="mt-6 space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Brain className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900 mb-1">
                          Assessment Information from File
                        </p>
                        <p className="text-sm text-blue-800">
                          Upload a CDISC file to automatically extract assessment details and items. The assessment name will be taken from the file.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {selectedLibrary === 'study' && !selectedStudy && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Please select a study above before uploading a CDISC file.
                      </p>
                    </div>
                  )}
                  <FileUploadZone 
                    onFileSelect={setUploadedFile}
                    onUpload={handleFileUpload}
                    uploading={uploading}
                    uploadError={uploadError}
                    selectedLibrary={selectedLibrary}
                    disabled={selectedLibrary === 'study' && !selectedStudy}
                  />
                </div>
              )}
            </div>

            {/* Assessment Information */}
            {(creationMethod === 'manual' || creationMethod === 'copy') && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assessment Name *
                    </label>
                    <input
                      type="text"
                      value={assessmentName}
                      onChange={(e) => {
                        copyFieldsTouched.current.name = true;
                        setAssessmentName(e.target.value);
                        // Clear error when user changes the name
                        if (uploadError && creationMethod === 'copy') {
                          setUploadError('');
                        }
                        // Clear local name error when typing
                        if (nameError) {
                          setNameError('');
                        }
                      }}
                      placeholder="e.g., PROMIS Physical Function 10a"
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        (uploadError && creationMethod === 'copy') || nameError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {/* Inline error message for duplicate name */}
                    {nameError && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {nameError}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Therapeutic Area
                    </label>
                    <input
                      type="text"
                      value={therapeuticArea}
                      onChange={(e) => {
                        copyFieldsTouched.current.therapeuticArea = true;
                        setTherapeuticArea(e.target.value);
                      }}
                      placeholder="e.g., Oncology, Pain Management"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => {
                        copyFieldsTouched.current.description = true;
                        setDescription(e.target.value);
                      }}
                      placeholder="Describe the purpose and content of this assessment..."
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  {/* FECOA-1526: COA Type (respondent type) — set at creation, locked once published */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      COA Type *
                    </label>
                    <select
                      value={respondentType}
                      onChange={(e) => {
                        copyFieldsTouched.current.respondentType = true;
                        setRespondentType(e.target.value);
                        if (respondentTypeError) setRespondentTypeError('');
                      }}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        respondentTypeError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select COA type...</option>
                      <option value="participant">PRO</option>
                      <option value="caregiver">ObsRO</option>
                      <option value="clinician">ClinRO</option>
                    </select>
                    {respondentTypeError && (
                      <p className="mt-2 text-sm text-red-600">{respondentTypeError}</p>
                    )}
                  </div>
                </div>
                
                {/* Error Message for Copy */}
                {creationMethod === 'copy' && uploadError && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-red-800">{uploadError}</p>
                      </div>
                      <button
                        onClick={() => setUploadError('')}
                        className="ml-3 text-red-600 hover:text-red-800"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Assessment Type Selection */}
            {creationMethod === 'manual' && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Type *</h3>
                <div className="space-y-3">
                  {assessmentTypes.map((type) => {
                    const IconComponent = type.icon;
                    const isSelected = assessmentType === type.id;
                    
                    return (
                      <div
                        key={type.id}
                        onClick={() => setAssessmentType(type.id)}
                        className={`
                          p-5 border rounded-xl cursor-pointer transition-all duration-300 flex items-start space-x-4
                          ${isSelected 
                            ? 'border-primary-500 bg-primary-50 shadow-sm' 
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 hover:shadow-sm dark:shadow-gray-900/20'
                          }
                        `}
                      >
                        <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-gray-900">{type.title}</h4>
                            {type.recommended && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                                {type.recommended}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </>
            ) : (
              <div className="space-y-6">
                {/* Select Primary Assessment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Primary Assessment *
                  </label>
                  <select
                    value={selectedPrimary}
                    onChange={(e) => setSelectedPrimary(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Choose a primary assessment...</option>
                    {primaryAssessments.map((primary) => (
                      <option key={primary.uuid} value={primary.uuid}>
                        {primary.name} (v{primary.version})
                      </option>
                    ))}
                  </select>
                  {loadingPrimaries ? (
                    <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                      <span>Loading primary assessments...</span>
                    </div>
                  ) : primaryAssessments.length === 0 ? (
                    <p className="mt-2 text-sm text-amber-600">
                      No primary assessments available. Create a primary first.
                    </p>
                  ) : null}
                </div>

                {/* Select Device */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Device *
                  </label>
                  <select
                    value={selectedDevice}
                    onChange={(e) => setSelectedDevice(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select device...</option>
                    <optgroup label="📱 Phones">
                      {devices.phone?.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name} ({device.screen_width}×{device.screen_height})
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="📱 Tablets">
                      {devices.tablet?.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name} ({device.screen_width}×{device.screen_height})
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="🖥️ Desktop">
                      {devices.desktop?.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name} ({device.screen_width}×{device.screen_height})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* Language Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language *
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {commonLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Error Message */}
                {variantError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-red-800">
                          {variantError}
                        </p>
                      </div>
                      <button
                        onClick={() => setVariantError('')}
                        className="ml-3 text-red-600 hover:text-red-800"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Variant Name (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variant Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={variantName}
                    onChange={(e) => {
                      setVariantName(e.target.value);
                      // Clear error when user starts typing a custom name
                      if (variantError && String(variantError).toLowerCase().includes('already exists')) {
                        setVariantError('');
                      }
                    }}
                    placeholder="Leave blank to auto-generate"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      variantError && String(variantError).toLowerCase().includes('already exists')
                        ? 'border-yellow-400 bg-yellow-50'
                        : 'border-gray-300'
                    }`}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If left blank, will auto-generate as: "{
                      primaryAssessments.find(p => p.uuid === selectedPrimary)?.name || 'Primary'
                    } - {selectedLanguage} ({
                      [...(devices.phone || []), ...(devices.tablet || []), ...(devices.desktop || [])]
                        .find(d => d.id === parseInt(selectedDevice))?.name || 'Device'
                    })"
                  </p>
                </div>

                {/* Copy Structure Checkbox */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="copy_structure"
                    checked={copyStructure}
                    onChange={(e) => setCopyStructure(e.target.checked)}
                    className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="copy_structure" className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">Copy structure from primary</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Recommended: Copies all screens and questions. Uncheck to start with empty variant.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700 mt-8">
              <button
                onClick={onBack}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </button>
              
              {variantMode === 'variant' ? (
                <button
                  onClick={handleNext}
                  disabled={!canProceed || uploading}
                  className={`
                    inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-lg transition-colors
                    ${canProceed && !uploading
                      ? 'text-white bg-purple-600 hover:bg-purple-700'
                      : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                    }
                  `}
                >
                  {uploading ? 'Creating…' : 'Create Variant'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              ) : creationMethod === 'copy' ? (
                <button
                  onClick={handleNext}
                  disabled={!canProceed || uploading}
                  className={`
                    inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-lg transition-colors
                    ${canProceed && !uploading
                      ? 'text-white bg-primary-600 hover:bg-primary-700'
                      : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                    }
                  `}
                >
                  {uploading ? 'Copying…' : 'Create Copy'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              ) : creationMethod === 'manual' && (
                <button
                  onClick={handleNext}
                  disabled={!canProceed || uploading}
                  className={`
                    inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-lg transition-colors
                    ${canProceed && !uploading
                      ? 'text-white bg-primary-600 hover:bg-primary-700'
                      : 'text-gray-400 bg-gray-200 dark:text-gray-500 dark:bg-gray-700 cursor-not-allowed'
                    }
                  `}
                >
                  {uploading ? 'Creating…' : 'Continue to Screen Builder'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-primary-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Assessment Preview</h2>
                  <p className="text-sm text-gray-600">View details before importing</p>
                </div>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Assessment Info */}
              <div className="bg-gradient-to-br from-blue-50 to-primary-50 rounded-lg p-5 border border-blue-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{previewData.name}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Version:</span>
                    <span className="ml-2 font-medium text-gray-900">v{previewData.version}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Library:</span>
                    <span className="ml-2 font-medium text-gray-900 capitalize">{previewData.library_type}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className={`ml-2 font-medium ${
                      previewData.publication_status === 'published' 
                        ? 'text-green-600' 
                        : 'text-amber-600'
                    }`}>
                      {previewData.publication_status === 'published' ? '✓ Published' : '📝 Draft'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium text-gray-900 capitalize">
                      {previewData.assessment_type?.replace('_', ' ') || 'Fixed Form'}
                    </span>
                  </div>
                </div>
                {previewData.description && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <p className="text-sm text-gray-700">{previewData.description}</p>
                  </div>
                )}
              </div>

              {/* Survey Structure */}
              {previewData.survey_detail && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <div className="flex items-center space-x-2 mb-4">
                    <Layers className="w-5 h-5 text-primary-600" />
                    <h4 className="font-semibold text-gray-900">Survey Structure</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total Screens</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {previewData.survey_detail.screens?.length || 0}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Assessment pages</p>
                    </div>
                    
                    <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total Items</span>
                        <span className="text-2xl font-bold text-primary-600">
                          {previewData.question_count || 0}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Questions & items</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Screen Details */}
              {previewData.survey_detail?.screens && previewData.survey_detail.screens.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                  <div className="flex items-center space-x-2 mb-4">
                    <ListChecks className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">Screen Breakdown</h4>
                  </div>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {previewData.survey_detail.screens.map((screen, index) => (
                      <div 
                        key={screen.id || index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-purple-600">
                              {screen.order_number || index + 1}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {screen.title || `Screen ${index + 1}`}
                            </p>
                            {screen.description && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">
                                {screen.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-gray-700">
                            {screen.elements?.filter(el => el.type === 'question')?.length || 0} items
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Metadata */}
              {(previewData.therapeutic_area || previewData.created_at) && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {previewData.therapeutic_area && (
                      <div>
                        <span className="text-gray-600">Therapeutic Area:</span>
                        <span className="ml-2 font-medium text-gray-900">{previewData.therapeutic_area}</span>
                      </div>
                    )}
                    {previewData.created_at && (
                      <div>
                        <span className="text-gray-600">Created:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {new Date(previewData.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end space-x-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  // Assessment is already selected, user can now proceed to copy
                }}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Use This Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Name Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Edit3 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Assessment Name Already Exists</h3>
                  <p className="text-sm text-gray-600">Please enter a different name</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {duplicateNameData?.userProvidedName ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    The name you provided (<strong>"{duplicateNameData?.originalName}"</strong>) already exists in this library.
                  </p>
                  <p className="text-sm text-gray-600">
                    Please choose a different name to continue with the import.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-700">
                  An assessment named <strong>"{duplicateNameData?.originalName}"</strong> already exists in this library. 
                  Please provide a unique name for the imported assessment.
                </p>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Assessment Name *
                </label>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Enter a unique name"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && renameValue.trim()) {
                      handleRetryWithNewName();
                    }
                  }}
                />
              </div>
              
              {uploadError && (
                <p className="text-sm text-red-600">{uploadError}</p>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setDuplicateNameData(null);
                  setRenameValue('');
                  setUploadError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRetryWithNewName}
                disabled={!renameValue.trim() || uploading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import with New Name
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibrarySelection;
